import classNames from 'classnames';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { Rarity, So5LeaderboardRarity } from '__generated__/globalTypes';
import { unitMapping } from 'lib/style';

import { User } from '../User';

type ScarcityType =
  | Lowercase<keyof typeof So5LeaderboardRarity>
  | keyof typeof Rarity;

export type Props = {
  scarcity: ScarcityType;
  children?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

const Icon = styled.span`
  display: inline-flex;
  border-radius: 2px;
  align-items: center;
  justify-content: center;
  color: var(--c-white);
  width: 1.5em;
  height: 2em;
  &.common {
    background: var(--c-gradient-common);
    color: var(--c-black);
  }
  &.custom_series {
    background: var(--c-gradient-customSeries);
  }
  &.limited {
    background: var(--c-gradient-limited);
  }
  &.mix {
    background: var(--c-gradient-mix);
  }
  &.rare {
    background: var(--c-gradient-rare);
  }
  &.rare_pro {
    background: var(--c-gradient-rare);
  }
  &.super_rare {
    background: var(--c-gradient-superRare);
  }
  &.unique {
    background: var(--c-gradient-unique);
  }
  &.sm {
    font-size: 7px;
  }
  &.md {
    font-size: 10px;
  }
  &.lg {
    font-size: 14px;
  }
  &.xl {
    font-size: 18px;
  }
`;

export const ScarcityIcon = ({
  scarcity,
  size = 'sm',
  children = <User />,
}: Props) => {
  return (
    <Icon aria-label={scarcity} className={classNames(scarcity, size)}>
      {children}
    </Icon>
  );
};

const colorsPerRarity = {
  [Rarity.common]: ['#D1DCDF', '#A9B1B5'],
  [Rarity.limited]: ['#D98C00', '#905D00'],
  [Rarity.rare]: ['#BF030E', '#900000'],
  [Rarity.super_rare]: ['#032AB9', '#17156D'],
  [Rarity.unique]: ['#3C3461', '#0E184A'],
};

type NewScarcityIconProps = {
  rarity: Rarity | 'mix';
  size?: keyof typeof unitMapping;
  className?: string;
};

export const NewScarcityIcon = ({
  rarity,
  size = 3,
  className,
}: NewScarcityIconProps) => {
  const [fill, gradient] =
    colorsPerRarity[
      rarity in colorsPerRarity
        ? (rarity as keyof typeof colorsPerRarity)
        : Rarity.unique
    ];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 14 24"
      fill="none"
      style={{ height: unitMapping[size] }}
      className={className}
    >
      <g>
        <path
          d="M-0.5 3C-0.5 1.34315 0.84315 0 2.5 0H11.5C13.1569 0 14.5 1.34315 14.5 3V21C14.5 22.6569 13.1569 24 11.5 24H2.5C0.84315 24 -0.5 22.6569 -0.5 21V3Z"
          fill={fill}
        />
        <path
          d="M-0.3125 3C-0.3125 1.4467 0.9467 0.1875 2.5 0.1875H11.5C13.0533 0.1875 14.3125 1.4467 14.3125 3V21C14.3125 22.5533 13.0533 23.8125 11.5 23.8125H2.5C0.9467 23.8125 -0.3125 22.5533 -0.3125 21V3Z"
          stroke="black"
          strokeOpacity="0.2"
          strokeWidth="0.375"
        />
        <path
          d="M0.0935059 14.3819V6.56536L7.24347 3.12817L14.1419 6.56536V14.3819L7.24347 17.6684L0.0935059 14.3819Z"
          fill="white"
          fillOpacity="0.3"
        />
        <path
          d="M14.1419 12.0528C13.7839 12.0528 14.0406 11.5507 13.6336 11.4236C13.3635 11.297 11.0449 10.3863 9.91937 9.94675C9.81277 9.88015 9.59397 9.60328 9.41887 9.38165C9.31667 9.25225 9.22927 9.14168 9.18777 9.10276C9.09777 9.01831 9.11277 8.1534 9.13147 7.7315C9.15027 7.46779 9.24407 7.0985 9.24407 7.0985C9.86307 6.8876 9.91937 5.88547 9.91937 5.88547C10.0545 5.37913 9.63797 5.35803 9.46917 5.56892V4.83059C9.46917 4.7884 9.39417 4.0746 9.35657 3.72297C9.35657 3.12521 8.62507 2.14065 7.10557 1.98242C5.30477 2.02462 4.77953 3.16037 4.74201 3.72297V4.77785L4.79829 5.56892C4.64822 5.27004 4.34808 5.41077 4.34808 5.83264C4.34808 6.76094 4.83581 7.06333 5.07967 7.0985C5.15467 7.29189 5.31607 7.73142 5.36107 7.9424C5.40607 8.15338 5.34227 8.80388 5.30477 9.10276L4.85456 9.89392C3.86036 10.2455 1.71439 11.0015 1.0841 11.2125C0.453806 11.4235 0.243576 12.1459 0.0935059 12.4447V13.4397V13.3744C0.0935059 14.1318 0.0935156 14.3518 0.999916 14.7458L7.10557 17.6985L13.3505 14.7458C14.1789 14.7458 14.1419 14.1318 14.1419 13.3744V12.0528Z"
          fill={`url(#card-gradient-${rarity})`}
        />
        <path
          d="M7.41446 18.3921L0.0605469 14.9851C0.0605469 15.9258 0.434287 16.1409 0.687117 16.2514L7.41446 19.5378C9.22826 18.6534 13.2779 16.4926 13.6473 16.2514C14.0166 16.0102 14.087 15.3871 14.109 14.9851L7.41446 18.3921Z"
          fill="white"
          fillOpacity="0.3"
        />
      </g>
      <defs>
        <linearGradient
          id={`card-gradient-${rarity}`}
          x1="7.11787"
          y1="0.482424"
          x2="7.11787"
          y2="17.6985"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={gradient} />
          <stop offset="1" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};
